import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import {
  Mail,
  FileText,
  Save,
  Clock,
  Plus,
  Trash2,
  Eye,
} from "lucide-react";

import { useGlobalContext } from "../pages/AUTH/GlobalContext";

const DEFAULT_TEMPLATE = `<div style="font-family: 'Segoe UI', Inter, sans-serif; color: #333; line-height: 1.6; max-width: 700px;">
  <p>Hi,</p>

  <p style="margin-top: 20px;">
    I'm reaching out to find in-office or remote full-time opportunities at your organization.
  </p>

  <p style="margin-top: 20px;">
    I'm attaching my resume for you to look over. I'd love to connect and talk about how my background could be a valuable addition to your team.
  </p>

  <p style="margin-top: 30px;">
    Kind Regards
  </p>
</div>`;

const DEFAULT_FOLLOW_UP =
  "Hi,\n\nI hope this email finds you well. I wanted to follow up on my previous application.\n\nI remain very interested in the opportunity and would love to discuss how my skills and experience could contribute to your team.\n\nLooking forward to hearing from you.\n\nBest regards";

const DEFAULT_SUBJECT = "Application For Engineering Roles (SDE-1)";

const TemplateEditor = () => {
  const { user } = useGlobalContext();

  const [template, setTemplate] = useState(DEFAULT_TEMPLATE);
  const [subject, setSubject] = useState(DEFAULT_SUBJECT);
  const [followUpTemplates, setFollowUpTemplates] = useState([
    { id: 1, name: "Follow-up 1", message: DEFAULT_FOLLOW_UP },
  ]);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [savingFollowUps, setSavingFollowUps] = useState(false);

  useEffect(() => {
    if (!user?.email) return;

    fetch(`${process.env.REACT_APP_BACKEND_BASE_URL}/api/email/template?email=${user.email}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.success && data.htmlEmailTemplate?.finalHtml) {
          setTemplate(data.htmlEmailTemplate.finalHtml);
        }
        if (data.success && data.htmlEmailTemplate?.subject) {
          setSubject(data.htmlEmailTemplate.subject);
        }
      })
      .catch((err) => console.log("Fetch template error:", err));

    fetch(`${process.env.REACT_APP_BACKEND_BASE_URL}/api/email/followup-templates/${user.email}`)
      .then((res) => res.json())
      .then((data) => {
        if (data.followupTemplates?.length > 0) {
          setFollowUpTemplates(data.followupTemplates);
        }
      })
      .catch((err) => console.log("Fetch follow-up templates error:", err));
  }, [user]);

  const addFollowUpTemplate = () => {
    const newId = Math.max(...followUpTemplates.map((t) => t.id), 0) + 1;
    setFollowUpTemplates((prev) => [
      ...prev,
      { id: newId, name: `Follow-up ${newId}`, message: DEFAULT_FOLLOW_UP },
    ]);
  };

  const updateFollowUpTemplate = (id, field, value) => {
    setFollowUpTemplates((prev) =>
      prev.map((t) => (t.id === id ? { ...t, [field]: value } : t))
    );
  };

  const removeFollowUpTemplate = (id) => {
    setFollowUpTemplates((prev) => prev.filter((t) => t.id !== id));
  };

  // Save Primary Template — this exact HTML is what every recruiter receives.
  const handleSaveTemplate = () => {
    if (!user?.email) return toast.error("User email missing.");

    setSavingTemplate(true);
    fetch(`${process.env.REACT_APP_BACKEND_BASE_URL}/api/email/template`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: user.email, finalHtml: template, subject }),
    })
      .then((r) => r.json())
      .then(() => toast.success("Saved — this is now sent to every recruiter you email."))
      .catch(() => toast.error("Failed to save template."))
      .finally(() => setSavingTemplate(false));
  };

  // Save Follow-Up Template Array
  const handleSaveFollowUps = () => {
    if (!user?.email) return toast.error("User email missing.");

    setSavingFollowUps(true);
    fetch(`${process.env.REACT_APP_BACKEND_BASE_URL}/api/email/followup-templates`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: user.email, followupTemplate: followUpTemplates }),
    })
      .then((r) => r.json())
      .then(() => toast.success("Follow-up templates saved!"))
      .catch(() => toast.error("Failed to save follow-ups."))
      .finally(() => setSavingFollowUps(false));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* HEADER */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-50 shrink-0">
              <Mail className="w-4 h-4 text-blue-600" />
            </span>
            <h1 className="text-xl font-bold text-gray-900">Email Template Editor</h1>
          </div>
          <p className="text-sm text-gray-500">
            Write this email once — it's the exact message every recruiter you reach out to will receive.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8 grid lg:grid-cols-2 gap-8 items-start">
        {/* LEFT */}
        <div className="space-y-6">
          {/* Main template */}
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6">
            <div className="flex items-center gap-2 mb-1">
              <FileText className="w-5 h-5 text-blue-600" />
              <h3 className="text-base font-semibold text-gray-900">Main Email Template</h3>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              This single template is sent to every recruiter you reach out to.
            </p>

            <label className="block text-xs font-medium text-gray-600 mb-1.5">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Email subject line"
              className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
            />

            <label className="block text-xs font-medium text-gray-600 mb-1.5">Body</label>
            <textarea
              value={template}
              onChange={(e) => setTemplate(e.target.value)}
              rows={14}
              spellCheck={false}
              className="w-full border border-gray-200 rounded-xl p-4 font-mono text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />

            <button
              onClick={handleSaveTemplate}
              disabled={savingTemplate}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-blue-600 text-white text-sm font-semibold px-4 py-3 rounded-xl shadow-sm shadow-blue-600/20 hover:bg-blue-700 disabled:opacity-60 transition-colors"
            >
              <Save size={16} />
              {savingTemplate ? "Saving..." : "Save Main Template"}
            </button>
          </div>

          {/* Follow-ups */}
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-orange-500" />
                <h3 className="text-base font-semibold text-gray-900">Follow-up Templates</h3>
              </div>
              <button
                onClick={addFollowUpTemplate}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-blue-600 hover:text-blue-700"
              >
                <Plus size={14} />
                Add
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              Used when you follow up with a recruiter who hasn't replied yet.
            </p>

            <div className="space-y-4">
              {followUpTemplates.map((t) => (
                <div key={t.id} className="border border-gray-200 bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    <input
                      type="text"
                      value={t.name}
                      onChange={(e) => updateFollowUpTemplate(t.id, "name", e.target.value)}
                      className="font-semibold text-sm text-gray-900 bg-transparent outline-none flex-1"
                    />
                    <button
                      onClick={() => removeFollowUpTemplate(t.id)}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>

                  <textarea
                    value={t.message}
                    onChange={(e) => updateFollowUpTemplate(t.id, "message", e.target.value)}
                    rows={4}
                    className="w-full border border-gray-200 rounded-lg p-3 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  />
                </div>
              ))}

              {followUpTemplates.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-6">No follow-up templates yet.</p>
              )}
            </div>

            <button
              onClick={handleSaveFollowUps}
              disabled={savingFollowUps}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-white text-gray-700 text-sm font-semibold px-4 py-3 rounded-xl border border-gray-200 hover:border-gray-300 hover:bg-gray-50 disabled:opacity-60 transition-colors"
            >
              <Save size={16} />
              {savingFollowUps ? "Saving..." : "Save Follow-Ups"}
            </button>
          </div>
        </div>

        {/* RIGHT — PREVIEW */}
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-6 lg:sticky lg:top-24">
          <div className="flex items-center gap-2 mb-4">
            <Eye className="w-5 h-5 text-blue-600" />
            <h3 className="text-base font-semibold text-gray-900">Live Preview</h3>
          </div>

          <div className="border border-gray-200 rounded-xl overflow-hidden">
            <div className="bg-gray-50 border-b border-gray-200 px-4 py-3 text-xs text-gray-500 space-y-1">
              <p><span className="font-medium text-gray-700">To:</span> recruiter@company.com</p>
              <p><span className="font-medium text-gray-700">Subject:</span> {subject || "(no subject)"}</p>
            </div>
            <div
              className="p-6 overflow-auto bg-white"
              style={{ maxHeight: "650px" }}
              dangerouslySetInnerHTML={{ __html: template }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateEditor;
